import React, { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon, MoreVertical } from "lucide-react";
import { useGetAuth } from "../../../hooks/useGetAuthHook";
import AlumniTableDetailModal from "../../../components/modals/AlumniTableDetailModal";
import { Alumni, PaginatedAlumni } from "../../../types/types";
import UpdateAlumniModal from "../../../components/modals/UpdateAlumniModal";
import { useNavigate } from "react-router-dom";

const Alumnis = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 4,
  });

  const [updateAlumni, setUpdateAlumni] = useState<Alumni | null>(null);
  const [selected, setSelected] = useState<Alumni | null>(null);

  const navigate = useNavigate();

  const { data, isLoading, error } = useGetAuth<PaginatedAlumni>("users", {
    params: {
      page: pagination.pageIndex + 1,
      per_page: pagination.pageSize,
    },
    queryKey: ["get-alumnis", pagination.pageIndex + 1, pagination.pageSize],
    placeholderData: keepPreviousData,
  });

  const handlePreviousPage = () => {
    if (pagination.pageIndex > 0) {
      setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 });
    }
  };

  const handleNextPage = () => {
    if (data && pagination.pageIndex < data.last_page - 1) {
      setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 });
    }
  };

  const canPreviousPage = pagination.pageIndex > 0;
  const canNextPage = data && pagination.pageIndex < data.last_page - 1;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading alumni...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Error loading alumni</p>
          <p className="text-red-600 text-sm mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto">
      {/* Cards Grid */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-orange-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {data?.data?.map((alumni) => (
            <div
              key={alumni.id}
              className="relative bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow duration-150 flex flex-col items-center"
            >
              <img
                src={alumni.photo_url}
                alt={alumni.name}
                className="w-20 h-20 rounded-full object-cover mb-4 border border-gray-200"
              />
              <h3
                className="text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-orange-500 text-center truncate max-w-full"
                onClick={() => navigate(`/dashboard/alumni/${alumni.id}`)}
              >
                {alumni.name}
              </h3>
              <button
                onClick={() => setSelected(alumni)}
                className="absolute top-4 right-4 p-1 rounded hover:bg-gray-100 transition-colors"
              >
                <MoreVertical size={18} className="text-gray-600" />
              </button>
            </div>
          ))}

          {/* Empty State */}
          {data?.data?.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <svg
                className="w-12 h-12 mx-auto mb-4 opacity-50 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H9v-1c0-2.21 1.79-4 4-4h2c2.21 0 4 1.79 4 4v1z"
                />
              </svg>
              <p className="text-gray-600 font-medium">No alumni found</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg p-4 border border-orange-100">
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-orange-600">
            Page {data?.current_page}
          </span>
          {" of "}
          <span className="font-semibold text-orange-600">
            {data?.last_page}
          </span>
          {data?.data && (
            <span className="text-gray-600 ml-4">
              {data.data.length} of {data.total} alumni
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePreviousPage}
            disabled={!canPreviousPage}
            className="inline-flex items-center gap-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors"
          >
            <ChevronLeftIcon size={18} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <button
            onClick={handleNextPage}
            disabled={!canNextPage}
            className="inline-flex items-center gap-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon size={18} />
          </button>
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <AlumniTableDetailModal
          alumni={selected}
          onClose={() => setSelected(null)}
          onUpdateClick={() => {
            setUpdateAlumni(selected);
            setSelected(null);
          }}
        />
      )}

      {updateAlumni && (
        <UpdateAlumniModal
          alumni={updateAlumni}
          onClose={() => setUpdateAlumni(null)}
          onSuccess={() => {
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default Alumnis;
